import { BankAccountVariable } from 'enums/LoanFormVariables';
import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import {
  exchangePublicToken,
  getCheckingAccounts,
  getSavingsAccounts,
  makeACHPayment,
  sendPlaidSuccess,
  updateApplicationBankAccountData,
  validateIdentity,
} from 'thunks';
import {
  BankAccountInfo,
  ExchangePublicTokenResponse,
  GetSavingsAccountsResponse,
  PlaidAccountError,
  SavingsAccount,
  SetBankAccountsError,
  SetBankAccountsErrorType,
} from 'api/PlaidApi';
import { MakeACHPaymentResponse } from 'api/PaymentApi';

export interface BankAccountDataForLos {
  [BankAccountVariable.AccountHolder]: string;
  [BankAccountVariable.BankName]: string;
  [BankAccountVariable.RoutingNumber]: string;
  [BankAccountVariable.AccountNumber]: string;
}

interface BankAccountData {
  [BankAccountVariable.AccountHolder]: string;
  [BankAccountVariable.BankName]: string;
  [BankAccountVariable.RoutingNumber]: string;
  [BankAccountVariable.AccountNumber]: string;
}

interface BankAccountState {
  isLoading: boolean;
  isLoadingCheckingAccounts: boolean;
  isLoadingSendPlaidSuccess: boolean;
  bankAccountData?: BankAccountData;
  bankAccounts: BankAccountInfo[];
  savingsAccounts: SavingsAccount[];
  missedPaymentAccountBalance?: number;
  plaidAccountError: PlaidAccountError | null;
}

const initialState: BankAccountState = {
  isLoading: false,
  isLoadingCheckingAccounts: false,
  isLoadingSendPlaidSuccess: false,
  bankAccounts: [],
  savingsAccounts: [],
  plaidAccountError: null,
};

const bankAccount = createSlice({
  name: 'bankAccount',
  initialState,
  reducers: {
    setBankAccountData: (state: BankAccountState, { payload }: PayloadAction<BankAccountData>) => {
      state.bankAccountData = payload;
    },
    setPlaidAccountError: (state: BankAccountState, { payload }: PayloadAction<PlaidAccountError | null>) => {
      state.plaidAccountError = payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(updateApplicationBankAccountData.fulfilled, (state) => {
      state.isLoading = false;
    });
    builder.addCase(updateApplicationBankAccountData.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(updateApplicationBankAccountData.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(getCheckingAccounts.pending, (state) => {
      state.isLoadingCheckingAccounts = true;
    });
    builder.addCase(getCheckingAccounts.fulfilled, (state, { payload }: PayloadAction<BankAccountInfo[]>) => {
      state.isLoadingCheckingAccounts = false;
      state.bankAccounts = payload;
    });
    builder.addCase(getCheckingAccounts.rejected, (state) => {
      state.isLoadingCheckingAccounts = false;
    });
    builder.addCase(makeACHPayment.fulfilled, (state, { payload }: PayloadAction<MakeACHPaymentResponse>) => {
      if (payload.bankAccountBalance) {
        state.missedPaymentAccountBalance = payload.bankAccountBalance;
      }
    });
    builder.addCase(exchangePublicToken.fulfilled, (state, { payload }: PayloadAction<ExchangePublicTokenResponse>) => {
      state.isLoading = false;
      if (payload.isSavingsAccount) {
        state.savingsAccounts = [
          ...state.savingsAccounts,
          {
            id: payload.accountId,
            name: payload.accountName,
            mask: payload.accountMask,
          },
        ];
      }
    });
    builder.addCase(exchangePublicToken.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(exchangePublicToken.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(getSavingsAccounts.pending, (state) => {
      state.isLoading = true;
    });
    builder.addCase(getSavingsAccounts.fulfilled, (state, { payload }: PayloadAction<GetSavingsAccountsResponse>) => {
      state.isLoading = false;
      state.savingsAccounts = payload.savingsAccounts;
    });
    builder.addCase(getSavingsAccounts.rejected, (state) => {
      state.isLoading = false;
    });
    builder.addCase(validateIdentity.fulfilled, (state) => {
      state.plaidAccountError = null;
    });
    builder.addCase(validateIdentity.rejected, (state, { payload }) => {
      const response = payload as SetBankAccountsError;
      state.plaidAccountError =
        response?.errorType === SetBankAccountsErrorType.AccountOwnerMismatchError
          ? PlaidAccountError.ACCOUNT_OWNER_MISMATCH_ERROR
          : PlaidAccountError.OTHER;
    });
    builder.addCase(sendPlaidSuccess.fulfilled, (state) => {
      state.plaidAccountError = null;
      state.isLoadingSendPlaidSuccess = false;
    });
    builder.addCase(sendPlaidSuccess.pending, (state) => {
      state.isLoadingSendPlaidSuccess = true;
    });
    builder.addCase(sendPlaidSuccess.rejected, (state, { payload }) => {
      const response = payload as SetBankAccountsError;
      state.plaidAccountError =
        response?.errorType === SetBankAccountsErrorType.AccountOwnerMismatchError
          ? PlaidAccountError.ACCOUNT_OWNER_MISMATCH_ERROR
          : PlaidAccountError.OTHER;
      state.isLoadingSendPlaidSuccess = false;
    });
  },
});

export const { setBankAccountData, setPlaidAccountError } = bankAccount.actions;

export default bankAccount.reducer;
